import React, { useCallback } from 'react';
import { Stack, Typography } from '@mui/material';
import { TextField, useForm } from '@travelity/form';
import { Button } from '@travelity/ui';
import { array, object, string } from 'yup';

const phoneRegExp = /^\+?\d{1,3} \d+$/;

const schema = object().shape({
    numbers: array()
        .of(
            object().shape({
                value: string()
                    .required('Phone number is required')
                    .matches(
                        phoneRegExp,
                        'Phone number must be in format: calling code (1-3 digits), space, number'
                    )
                    .min(6, 'Phone number is too short')
                    .max(17, 'Phone number is too long'),
            })
        )
        .min(1, 'Phone number is required')
        .required('Phone number is required'),
});

export interface PhoneNumberProps {
    numbers?: { value: string }[];
    back: () => void;
    next: (v: { value: string }[]) => void;
}

export const PhoneNumber: React.FC<PhoneNumberProps> = props => {
    const { numbers, next, back } = props;

    const [showError, setShowErrors] = React.useState(false);

    const { Form, watch } = useForm({
        schema,
        onSubmit: useCallback(
            (data: { numbers: { value: string }[] }) => {
                next(data.numbers);
            },
            [next]
        ),
        defaultValues: {
            numbers: numbers?.length ? numbers : [{ value: '' }],
        },
        mode: 'onChange',
        validateInitially: true,
    });

    return (
        <Form>
            <Stack
                sx={{
                    py: 5,
                    height: 1,
                    minHeight: 'calc(100vh - 84px)',
                }}
                gap={2}
                alignItems="center"
                justifyContent="center"
            >
                <Typography
                    sx={{
                        color: '#C0C4CE',
                        fontSize: { xs: '20px', sm: '24px' },
                    }}
                >
                    What is your phone number?
                </Typography>
                <TextField
                    name="numbers.0.value"
                    label=""
                    placeholder="Type calling code and number (e.g. 374 99123456)"
                    regExp={/^\+?\d{0,3}( \d*)?$/}
                    showErrors={showError}
                />
                <Typography
                    sx={{
                        width: { xs: '100%', sm: '388px' },
                        color: '#8B93A7',
                        fontSize: '12px',
                        lineHeight: '16px',
                    }}
                >
                    Enter phone as calling code, then space, then number.
                    Example: 123 012345678
                </Typography>
                <Stack
                    direction="row"
                    spacing={1}
                    sx={{ width: { xs: '100%', sm: '388px' } }}
                >
                    <Button
                        variant="contained"
                        color="neutral"
                        sx={{ flexGrow: 1, px: 4 }}
                        onClick={back}
                    >
                        Back
                    </Button>
                    <Button
                        color="secondary"
                        variant="contained"
                        type="submit"
                        sx={{
                            px: 10,
                            py: 1,
                        }}
                        doNotDisable
                        onIconClick={() => {
                            setShowErrors(true);
                        }}
                        requiredFields={
                            !watch('numbers')?.[0]?.value?.match(phoneRegExp)
                                ? ['Phone number']
                                : undefined
                        }
                    >
                        Continue
                    </Button>
                </Stack>
            </Stack>
        </Form>
    );
};
